// Copyright 2025, University of Maryland, All Rights Reserved
import type { NodeApiClient } from '@/app-shell/services'
import type { WorkspaceNodeInstance, WorkspaceNodeInstanceMap, WorkspaceNodesExtended } from '@/subsystems/palette'
import { nodeInstancePath, type NodeInstanceScope } from './node-instance.service'

/**
 * Response from GET /pipeline-nodes - lists all pipeline node instances for a workspace.
 */
interface GetWorkspaceNodesResponse {
  workspaceId: string
  hasNodes: boolean
  instances: WorkspaceNodeInstanceMap
}

/**
 * Response containing a single node instance.
 */
interface NodeInstanceResponse {
  instance: WorkspaceNodeInstance | null
}

/**
 * Chunk metadata for node instance data.
 */
export interface NodeInstanceDataChunk {
  chunk_id?: string | null
  index?: number | null
  size_bytes?: number | null
  source_file?: string | null
}

/**
 * Response from fetching node instance data.
 */
export interface NodeInstanceDataResponse<TData = Record<string, unknown>> {
  instance: WorkspaceNodeInstance
  data: TData
  chunk_summary: {
    chunk_count: number
    chunks: NodeInstanceDataChunk[]
  }
}

/**
 * Service for workspace node configuration operations.
 */
export class NodeConfigService {
  constructor(private readonly client: NodeApiClient) {}

  /** Build URL path to a specific pipeline node instance */
  private buildInstancePath(
    orgId: string,
    workspaceId: string,
    pipelineId: string,
    instanceId: string,
    scope?: NodeInstanceScope,
  ): string {
    return nodeInstancePath(orgId, workspaceId, pipelineId, instanceId, scope)
  }

  async fetch(orgId: string, workspaceId: string): Promise<WorkspaceNodesExtended | null> {
    const { data } = await this.client.get<GetWorkspaceNodesResponse>(
      `/workspaces/${orgId}/${workspaceId}/pipeline-nodes`
    )

    if (!data.hasNodes) {
      return null
    }

    const instances: WorkspaceNodeInstanceMap = data.instances ?? {}
    const sanitizedInstances: WorkspaceNodeInstanceMap = {}

    // Strip 'data' field from instances - it's fetched separately via fetchInstanceData()
    for (const [instanceId, instance] of Object.entries(instances)) {
      const { data: _, ...rest } = instance as WorkspaceNodeInstance & { data?: unknown }
      sanitizedInstances[instanceId] = rest
    }

    return {
      instances: sanitizedInstances,
    }
  }

  async save(orgId: string, workspaceId: string, payload: WorkspaceNodesExtended): Promise<void> {
    await this.client.post(`/workspaces/${orgId}/${workspaceId}/pipeline-nodes`, payload)
  }

  async fetchInstanceMetadata(
    orgId: string,
    workspaceId: string,
    instanceId: string,
  ): Promise<WorkspaceNodeInstance | null> {
    const { data } = await this.client.get<NodeInstanceResponse>(
      `/workspaces/${orgId}/${workspaceId}/pipeline-nodes/${instanceId}`,
    )
    return data.instance ?? null
  }

  async fetchInstanceData<TData = Record<string, unknown>>(
    orgId: string,
    workspaceId: string,
    pipelineId: string,
    instanceId: string,
  ): Promise<NodeInstanceDataResponse<TData>> {
    const basePath = this.buildInstancePath(orgId, workspaceId, pipelineId, instanceId)
    const { data } = await this.client.get<NodeInstanceDataResponse<TData>>(`${basePath}/data`)
    return data
  }

  /**
   * Delete a node instance from the workspace.
   * Used to reset inherited nodes back to base configuration.
   */
  async deleteNodeInstance(
    orgId: string,
    workspaceId: string,
    pipelineId: string,
    instanceId: string,
    scope?: NodeInstanceScope,
  ): Promise<void> {
    await this.client.delete(
      this.buildInstancePath(orgId, workspaceId, pipelineId, instanceId, scope)
    )
  }
}

